import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaShoppingCart, FaTimes } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const FloatingCartButton = styled.button`
  position: fixed;
  bottom: 100px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #39ff14, #00ccff);
  border: 2px solid rgba(0, 255, 255, 0.4);
  box-shadow: 
    0 0 20px rgba(0, 255, 255, 0.6),
    0 0 40px rgba(0, 255, 0, 0.4),
    0 4px 15px rgba(0, 0, 0, 0.3);
  color: #0a0f1c;
  font-size: 24px;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  animation: ${pulse} 2s ease-in-out infinite;

  &:hover {
    transform: scale(1.1);
    box-shadow: 
      0 0 30px rgba(0, 255, 255, 0.8),
      0 0 60px rgba(0, 255, 0, 0.6),
      0 6px 20px rgba(0, 0, 0, 0.4);
  }

  @media (max-width: 768px) {
    bottom: 80px;
    right: 15px;
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

const CartBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ff4757;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  border: 2px solid #0a0f1c;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const CartModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const CartContent = styled.div`
  background: #0D111B;
  border-radius: 24px;
  border: 2px solid rgba(0, 255, 255, 0.4);
  box-shadow: 
    0 0 40px rgba(0, 255, 255, 0.6),
    0 0 80px rgba(0, 255, 0, 0.4),
    0 0 120px rgba(255, 0, 128, 0.2);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  position: relative;
`;

const CartHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(0, 255, 255, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 255, 255, 0.1);
`;

const CartTitle = styled.h3`
  color: #00ccff;
  margin: 0;
  font-size: 20px;
  text-shadow: 0 0 10px rgba(0, 204, 255, 0.8);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #00ccff;
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 204, 255, 0.2);
    transform: scale(1.1);
  }
`;

const CartItems = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 20px;
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 12px;
  margin-bottom: 10px;
  background: rgba(0, 255, 255, 0.05);
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid rgba(0, 255, 255, 0.3);
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemTitle = styled.h4`
  color: #ffffff;
  margin: 0 0 5px 0;
  font-size: 16px;
`;

const ItemPrice = styled.p`
  color: #39ff14;
  margin: 0;
  font-weight: bold;
  text-shadow: 0 0 5px rgba(57, 255, 20, 0.8);
`;

const ItemDates = styled.p`
  color: #b0eaff;
  margin: 5px 0 0 0;
  font-size: 12px;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuantityButton = styled.button`
  background: rgba(0, 255, 255, 0.2);
  border: 1px solid rgba(0, 255, 255, 0.4);
  color: #00ccff;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const Quantity = styled.span`
  color: #ffffff;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: rgba(255, 0, 128, 0.2);
  border: 1px solid rgba(255, 0, 128, 0.4);
  color: #ff0080;
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 0, 128, 0.3);
    transform: scale(1.05);
  }
`;

const CartFooter = styled.div`
  padding: 20px;
  border-top: 1px solid rgba(0, 255, 255, 0.3);
  background: rgba(0, 255, 255, 0.1);
`;

const CartTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TotalLabel = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: bold;
`;

const TotalAmount = styled.span`
  color: #39ff14;
  font-size: 24px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(57, 255, 20, 0.8);
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #39ff14, #00ccff);
  border: none;
  border-radius: 12px;
  color: #0a0f1c;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
      box-shadow: 0 0 20px rgba(57, 255, 20, 0.45);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 30px rgba(57, 255, 20, 0.6);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #b0eaff;
`;

export default function FloatingCart() {
  const { items, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/cart-checkout');
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateItemTotal = (item) => {
    const startDate = new Date(item.startDate);
    const endDate = new Date(item.endDate);
    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    return item.price * days * item.quantity;
  };

  return (
    <>
      <FloatingCartButton onClick={() => setIsOpen(true)}>
        <FaShoppingCart />
        {getCartCount() > 0 && <CartBadge>{getCartCount()}</CartBadge>}
      </FloatingCartButton>

      {isOpen && (
        <CartModal onClick={() => setIsOpen(false)}>
          <CartContent onClick={(e) => e.stopPropagation()}>
            <CartHeader>
              <CartTitle>Shopping Cart</CartTitle>
              <CloseButton onClick={() => setIsOpen(false)}>
                <FaTimes />
              </CloseButton>
            </CartHeader>

            <CartItems>
              {items.length === 0 ? (
                <EmptyCart>
                  <h3>Your cart is empty</h3>
                  <p>Add some inflatables to get started!</p>
                </EmptyCart>
              ) : (
                items.map((item, index) => (
                  <CartItem key={`${item.id}-${item.startDate}-${item.endDate}-${index}`}>
                    <ItemImage 
                      src={item.image || '/images/placeholder.svg'} 
                      alt={item.title}
                    />
                    <ItemDetails>
                      <ItemTitle>{item.title}</ItemTitle>
                      <ItemPrice>{formatMoney(calculateItemTotal(item))}</ItemPrice>
                      <ItemDates>
                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                      </ItemDates>
                    </ItemDetails>
                    <QuantityControl>
                      <QuantityButton 
                        onClick={() => updateQuantity(item, item.quantity - 1)}
                      >
                        -
                      </QuantityButton>
                      <Quantity>{item.quantity}</Quantity>
                      <QuantityButton 
                        onClick={() => updateQuantity(item, item.quantity + 1)}
                      >
                        +
                      </QuantityButton>
                    </QuantityControl>
                    <RemoveButton onClick={() => removeFromCart(item)}>
                      Remove
                    </RemoveButton>
                  </CartItem>
                ))
              )}
            </CartItems>

            {items.length > 0 && (
              <CartFooter>
                <CartTotal>
                  <TotalLabel>Total:</TotalLabel>
                  <TotalAmount>{formatMoney(getCartTotal())}</TotalAmount>
                </CartTotal>
                <CheckoutButton onClick={handleCheckout}>
                  Proceed to Checkout
                </CheckoutButton>
              </CartFooter>
            )}
          </CartContent>
        </CartModal>
      )}
    </>
  );
}
